import { useMemo, useState } from "react";
import { Mail, Eye, EyeOff, MailPlus, Inbox } from "lucide-react";
import { format, isSameWeek, isToday } from "date-fns";
import { useTranslation } from "react-i18next";
import Avatar from "../../../components/Avatar";
import GroupedSectionHeader from "../../../components/ui/GroupedSectionHeader";
import { getBaseUrl } from "../../../api/api";
import { VisibilityBadge } from "../../../activities/ActivityView";

export type EmailRow = {
  id: string | number; // log id
  subject: string;
  to: string; // comma separated for fallback
  preview?: string;
  timeLabel?: string; // human string (e.g., "1 day ago")
  createdAt?: number; // seconds epoch for grouping
  // Rich data
  recipients?: string[]; // To list (names without @ prefix)
  senderName?: string;
  senderAvatar?: string;
  trackingEnabled?: boolean;
  visibility?: "Shared" | "Private"; // Email visibility status
  // Optional message context for opening
  messageId?: string;
  threadId?: string;
  provider?: "GMAIL" | "OUTLOOK" | string;
  isLogEmail?: boolean;
  isRecordSentEmail?: boolean;
};

export interface EmailsSectionProps {
  title?: string;
  emails: EmailRow[];
  hasGmail?: boolean;
  hasOutlook?: boolean;
  onOpen: (id: string | number) => void;
  onLogEmail: () => void;
  onComposeEmail: () => void;
  onTrackingEmail: (messageId: string, threadId: string) => void;
}

// Group emails into Today, This week, and by Month (e.g., "November 2025")
function groupEmails(emails: EmailRow[]) {
  const today: EmailRow[] = [];
  const thisWeek: EmailRow[] = [];
  const months: Record<string, EmailRow[]> = {};

  const now = new Date();
  const weekOptions = { weekStartsOn: 1 as const };

  emails.forEach((e) => {
    const dt = e.createdAt ? new Date(e.createdAt * 1000) : undefined;
    if (!dt) {
      // No timestamp -> put into This week (fallback)
      thisWeek.push(e);
      return;
    }
    if (isToday(dt)) {
      today.push(e);
      return;
    }
    if (isSameWeek(dt, now, weekOptions)) {
      thisWeek.push(e);
      return;
    }
    const label = format(dt, "MMMM yyyy");
    if (!months[label]) months[label] = [];
    months[label].push(e);
  });

  return { today, thisWeek, months };
}

export function RecipientText({
  recipients,
  fallback,
}: {
  recipients?: string[];
  fallback?: string;
}) {
  const text = useMemo(() => {
    if (recipients && recipients.length > 0) {
      return recipients.map((r) => `@${r}`).join(" ");
    }
    if (fallback) {
      // Parse fallback to add @ prefix if not present
      return fallback
        .split(",")
        .map((r) => r.trim())
        .map((r) => (r.startsWith("@") ? r : `@${r}`))
        .join(" ");
    }
    return "";
  }, [recipients, fallback]);
  return (
    <span title={text} className="text-xs font-medium text-blue-600 leading-4 truncate">
      {text}
    </span>
  );
}

export function EmailRowComponent({
  email: e,
  onOpen,
  onTrackingEmail,
  showTimeFirst = false,
}: {
  email: EmailRow;
  onOpen: (id: string | number) => void;
  onTrackingEmail: (messageId: string, threadId: string) => void;
  showTimeFirst?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div
      className="flex items-center justify-between gap-2 w-full cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5 transition-colors"
      onClick={() => onOpen(e.id)}
    >
      {/* Time - displayed at the front */}
      {showTimeFirst && e.timeLabel && (
        <div className="flex-1 text-[10px] text-gray-500">{e.timeLabel}</div>
      )}

      {/* Subject Section */}
      <div className="flex items-center gap-1 flex-2 min-w-0">
        {e.isLogEmail ? (
          <Inbox className="w-4 h-4 text-gray-500 shrink-0" />
        ) : (
          <Mail className="w-4 h-4 text-gray-500 shrink-0" />
        )}
        <p className="text-xs font-medium text-gray-800 leading-4 truncate" title={e.subject}>
          {e.subject || t("No Subject")}
        </p>
      </div>

      {/* Sender/Recipients Section */}
      <div className="flex items-center gap-1 flex-3">
        <Avatar avatarUrl={e.senderAvatar} displayName={e.senderName || ""} sizeIcon={5} />
        {e.senderName && (
          <p className="text-xs font-medium text-gray-800 leading-4 whitespace-nowrap">
            {e.senderName}
          </p>
        )}
        <p className="text-xs font-normal text-gray-500 leading-4 whitespace-nowrap">
          {e.isLogEmail ? t("log email") : t("sent email")}
        </p>
        <RecipientText recipients={e.recipients} fallback={e.to} />
      </div>

      {/* Tracking Badge */}
      <div className="flex items-center flex-1">
        {e.isLogEmail ? (
          <div></div>
        ) : (
          <>
            {e.trackingEnabled ? (
              <div
                className="cursor-pointer"
                onClick={(event) => {
                  event.stopPropagation();
                  onTrackingEmail(e.messageId, e.threadId);
                }}
              >
                <Eye className="w-4 h-4 text-blue-800 flex-shrink-0" />
              </div>
            ) : (
              <div>
                <EyeOff className="w-4 h-4 text-gray-800 flex-shrink-0" />
              </div>
            )}
          </>
        )}
      </div>

      {/* Visibility Badge */}
      <div className="flex items-center flex-1">
        {e.isLogEmail ? <div></div> : <VisibilityBadge visibility={e.visibility} />}
      </div>

      {/* Time - displayed at the end (default variant) */}
      {!showTimeFirst && e.timeLabel && (
        <div className="flex-1 text-right text-[10px] text-gray-500">{e.timeLabel}</div>
      )}
    </div>
  );
}

export default function EmailsSection({
  title = "Email",
  emails,
  hasGmail,
  hasOutlook,
  onOpen,
  onLogEmail,
  onComposeEmail,
  onTrackingEmail,
}: EmailsSectionProps) {
  const { t } = useTranslation();
  const groups = useMemo(() => groupEmails(emails), [emails]);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const renderIconComposeEmail = () => {
    if (hasGmail) {
      return (
        <img
          className="w-4 h-4 object-contain"
          src={`${getBaseUrl()}/static/images/google.png`}
          alt="Google"
        />
      );
    }
    if (hasOutlook) {
      return (
        <img
          className="w-4 h-4 object-contain"
          src={`${getBaseUrl()}/static/images/microsoft.png`}
          alt="Microsoft"
        />
      );
    }
    return null;
  };

  const handleGroupToggle = (label: string) => {
    const newCollapsed = new Set(collapsedGroups);
    if (newCollapsed.has(label)) {
      newCollapsed.delete(label);
    } else {
      newCollapsed.add(label);
    }
    setCollapsedGroups(newCollapsed);
  };

  const renderEmailRow = (e: EmailRow) => (
    <EmailRowComponent key={e.id} email={e} onOpen={onOpen} onTrackingEmail={onTrackingEmail} />
  );

  const renderGroup = (label: string, items: EmailRow[]) => {
    const isCollapsed = collapsedGroups.has(label);
    return (
      <div key={label} className="flex flex-col items-start w-full">
        <GroupedSectionHeader
          label={label}
          count={items.length}
          isCollapsed={isCollapsed}
          onToggle={() => handleGroupToggle(label)}
          toggleAriaLabel={isCollapsed ? (t("Expand") as string) : (t("Collapse") as string)}
        />
        {!isCollapsed && (
          <div className="w-full flex flex-col gap-1 mt-1">{items.map(renderEmailRow)}</div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-start justify-between pb-2">
        <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onLogEmail}
            className="inline-flex items-center gap-1 px-1.5 py-1.5 border border-gray-200 rounded-md bg-white shadow-sm hover:bg-gray-50 transition-colors"
          >
            <MailPlus className="h-4 w-4 text-gray-800" />
            <span className="text-xs font-medium text-gray-800">{t("Log Email")}</span>
          </button>
          <button
            type="button"
            onClick={onComposeEmail}
            className="inline-flex items-center gap-1 px-1.5 py-1.5 border border-gray-200 rounded-md bg-white shadow-sm hover:bg-gray-50 transition-colors"
          >
            {renderIconComposeEmail()}
            <span className="text-xs font-medium text-gray-800">{t("Compose Email")}</span>
          </button>
        </div>
      </div>

      {/* Email Groups */}
      <div className="flex flex-col gap-5 mt-6">
        {/* Today */}
        {groups.today.length > 0 && renderGroup(t("Today"), groups.today)}

        {/* This week */}
        {groups.thisWeek.length > 0 && renderGroup(t("This week"), groups.thisWeek)}

        {/* Months */}
        {Object.entries(groups.months).map(([label, items]) => renderGroup(label, items))}

        {/* Empty */}
        {emails.length === 0 && (
          <div className="py-8 text-center text-sm text-gray-600">{t("No emails yet")}</div>
        )}
      </div>
    </div>
  );
}
